import { ReactElement, cloneElement, isValidElement } from 'react';
import { createPortal } from 'react-dom';
import Tooltip from '@/components/Tooltip';
import PanelLoadingBoundary from '@/components/PanelLoadingBoundary';
import { FullscreenEnterIcon, FullscreenExitIcon } from '@/components/icons/FullscreenIcons';
import { LockIcon } from '@/components/icons/LockIcon';

type PanelIdSet = ReadonlySet<string> | Set<string>;

type ComponentMap = Record<string, React.ReactNode>;

type FullscreenPortalArgs = {
    fullScreenPanelId: string | null;
    eligiblePanelIds: PanelIdSet;
    menuPanelIds: PanelIdSet;
    componentMap: ComponentMap;
    fullscreenExitLabel: string;
    onEnterFullScreen: (panelId: string) => void;
    onExitFullScreen: () => void;
};

/**
 * 全画面表示ポータルを作成する
 */
export const createFullscreenPortal = ({
    fullScreenPanelId,
    eligiblePanelIds,
    menuPanelIds,
    componentMap,
    fullscreenExitLabel,
    onEnterFullScreen,
    onExitFullScreen,
}: FullscreenPortalArgs): React.ReactNode => {
    const isFullScreenEligible =
        fullScreenPanelId !== null && eligiblePanelIds.has(fullScreenPanelId);
    const fullScreenPanelNode =
        isFullScreenEligible && fullScreenPanelId && componentMap[fullScreenPanelId]
            ? componentMap[fullScreenPanelId]
            : null;

    const hasMenu = fullScreenPanelId ? menuPanelIds.has(fullScreenPanelId) : false;

    if (
        !fullScreenPanelId ||
        !isFullScreenEligible ||
        !fullScreenPanelNode ||
        !isValidElement(fullScreenPanelNode)
    ) {
        return null;
    }

    return createPortal(
        <div
            className="panel-fullscreen-backdrop"
            role="dialog"
            aria-modal="true"
            data-panel-id={fullScreenPanelId}
            onClick={onExitFullScreen}
        >
            <div
                className="panel-fullscreen-content"
                data-panel-id={fullScreenPanelId}
                onClick={(event) => event.stopPropagation()}
            >
                {!hasMenu && (
                    <Tooltip
                        content={fullscreenExitLabel}
                        className="z-[2100] select-none rounded-md bg-gray-800 text-white text-xs px-2 py-1 shadow-lg border border-black/10"
                    >
                        <button
                            type="button"
                            className="panel-fullscreen-toggle panel-fullscreen-toggle--overlay"
                            onClick={onExitFullScreen}
                            aria-label={fullscreenExitLabel}
                        >
                            <FullscreenExitIcon className="panel-fullscreen-icon" />
                        </button>
                    </Tooltip>
                )}
                <PanelLoadingBoundary
                    wrapperClassName="w-full h-full"
                    className="flex flex-col justify-center items-center h-full w-full"
                >
                    {cloneElement(
                        fullScreenPanelNode as ReactElement<Record<string, unknown>>,
                        {
                            key: `${fullScreenPanelId}-fullscreen`,
                            onEnterFullScreen: () => {
                                if (fullScreenPanelId) {
                                    onEnterFullScreen(fullScreenPanelId);
                                }
                            },
                            onExitFullScreen,
                            isFullScreen: true,
                        } as Record<string, unknown>,
                    )}
                </PanelLoadingBoundary>
            </div>
        </div>,
        document.body,
    );
};

type PanelRenderInfoArgs = {
    panelId: string;
    panel: React.ReactNode;
    fullScreenPanelId: string | null;
    eligiblePanelIds: PanelIdSet;
    menuPanelIds: PanelIdSet;
    onEnterFullScreen: (panelId: string) => void;
    onExitFullScreen: () => void;
};

export const getPanelRenderInfo = ({
    panelId,
    panel,
    fullScreenPanelId,
    eligiblePanelIds,
    menuPanelIds,
    onEnterFullScreen,
    onExitFullScreen,
}: PanelRenderInfoArgs) => {
    const isFullScreenEnabled = eligiblePanelIds.has(panelId);
    const isFullScreen = isFullScreenEnabled && fullScreenPanelId === panelId;
    const shouldRenderDefaultFullscreenButton = isFullScreenEnabled && !menuPanelIds.has(panelId);

    const panelElement =
        isFullScreenEnabled && isValidElement(panel)
            ? cloneElement(
                  panel as ReactElement<Record<string, unknown>>,
                  {
                      onEnterFullScreen: () => onEnterFullScreen(panelId),
                      onExitFullScreen,
                      isFullScreen,
                  } as Record<string, unknown>,
              )
            : panel;

    return { panelElement, isFullScreenEnabled, isFullScreen, shouldRenderDefaultFullscreenButton };
};

type FullscreenToggleButtonProps = {
    isFullScreen: boolean;
    onEnter: () => void;
    onExit: () => void;
    enterLabel: string;
    exitLabel: string;
    isLocked?: boolean;
    lockedTooltip?: string;
    onLockedClick?: () => void;
};

export const FullscreenToggleButton = ({
    isFullScreen,
    onEnter,
    onExit,
    enterLabel,
    exitLabel,
    isLocked,
    lockedTooltip,
    onLockedClick,
}: FullscreenToggleButtonProps) => {
    const label = isFullScreen ? exitLabel : enterLabel;

    const button = (
        <button
            type="button"
            className="panel-fullscreen-toggle"
            onClick={isLocked ? onLockedClick : isFullScreen ? onExit : onEnter}
            aria-label={label}
        >
            {/* 元のフルスクリーンアイコンは常に表示 */}
            {isFullScreen ? (
                <FullscreenExitIcon className="panel-fullscreen-icon" />
            ) : (
                <FullscreenEnterIcon className="panel-fullscreen-icon" />
            )}
            {/* ロック中は右上に小さなロックアイコン（白い円背景）を重ねる */}
            {isLocked && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-white shadow border border-gray-200">
                    <LockIcon className="h-3 w-3 text-gray-500" />
                </span>
            )}
        </button>
    );

    if (isLocked && lockedTooltip) {
        return <Tooltip content={lockedTooltip}>{button}</Tooltip>;
    }

    return button;
};
